import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {   RiAddLine, RiEditLine, RiDeleteBinLine, RiPriceTag3Line,   RiLoader4Line, RiCloseLine, RiSaveLine, RiSearchLine  } from 'react-icons/ri';
import { API_BASE_URL } from '../../config';

const ManageTravel = () => {
    const [activeTab, setActiveTab] = useState('places'); // 'places' or 'categories'
    const [places, setPlaces] = useState([]);
    const [categories, setCategories] = useState([]);
    const [districts, setDistricts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');

    // Modals
    const [showPlaceModal, setShowPlaceModal] = useState(false);
    const [editingPlace, setEditingPlace] = useState(null);
    const [newPlace, setNewPlace] = useState({
        district_id: '', category_id: '', name: '', description: '',
        image_url: '', map_location: '', content: ''
    });

    const [showCategoryModal, setShowCategoryModal] = useState(false);
    const [newCategory, setNewCategory] = useState({ name: '' });
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        fetchAllData();
    }, []);

    const fetchAllData = async () => {
        setLoading(true);
        try {
            const [pRes, cRes, dRes] = await Promise.all([
                fetch(`${API_BASE_URL}/travel/places/read.php`),
                fetch(`${API_BASE_URL}/travel/categories/read.php`),
                fetch(`${API_BASE_URL}/districts/read_all.php`)
            ]);

            const pData = await pRes.json();
            const cData = await cRes.json();
            const dData = await dRes.json();

            setPlaces(pData.records || []);
            setCategories(cData.records || []);
            setDistricts(dData.records || []);
        } catch (error) {
            console.error('Error fetching travel data:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSavePlace = async (e) => {
        e.preventDefault();
        setSaving(true);
        const url = editingPlace ? `${API_BASE_URL}/travel/places/update.php` : `${API_BASE_URL}/travel/places/create.php`;
        const payload = editingPlace ? editingPlace : newPlace;

        try {
            const response = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            if (response.ok) {
                setShowPlaceModal(false);
                setEditingPlace(null);
                setNewPlace({ district_id: '', category_id: '', name: '', description: '', image_url: '', map_location: '', content: '' });
                fetchAllData();
            }
        } catch (error) {
            console.error('Error saving place:', error);
        } finally {
            setSaving(false);
        }
    };

    const handleDeletePlace = async (id) => {
        if (!window.confirm("Are you sure you want to delete this destination?")) return;
        try {
            await fetch(`${API_BASE_URL}/travel/places/delete.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id })
            });
            fetchAllData();
        } catch (error) {
            console.error('Error deleting place:', error);
        }
    };

    const handleSaveCategory = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const response = await fetch(`${API_BASE_URL}/travel/categories/create.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(newCategory)
            });
            if (response.ok) {
                setShowCategoryModal(false);
                setNewCategory({ name: '' });
                fetchAllData();
            }
        } catch (error) {
            console.error('Error saving category:', error);
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteCategory = async (id) => {
        if (!window.confirm("Are you sure? Categories with active places cannot be deleted.")) return;
        try {
            const response = await fetch(`${API_BASE_URL}/travel/categories/delete.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id })
            });
            if (response.ok) fetchAllData();
            else {
                const data = await response.json();
                alert(data.message);
            }
        } catch (error) {
            console.error('Error deleting category:', error);
        }
    };

    const filteredPlaces = places.filter(p =>
        p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.district_name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="p-8">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6 mb-12">
                <div>
                    <h1 className="text-2xl font-bold text-slate-900">Travel & Destinations</h1>
                    <p className="text-slate-500">Manage cultural sites, wildlife hotspots, and hidden gems across Sri Lanka.</p>
                </div>
                <div className="flex bg-slate-100 p-1.5 rounded-2xl">
                    <button
                        onClick={() => setActiveTab('places')}
                        className={`px-6 py-2.5 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${activeTab === 'places' ? 'bg-white shadow-sm text-emerald-600' : 'text-slate-400 hover:text-slate-600'}`}
                    >
                        Destinations
                    </button>
                    <button
                        onClick={() => setActiveTab('categories')}
                        className={`px-6 py-2.5 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${activeTab === 'categories' ? 'bg-white shadow-sm text-emerald-600' : 'text-slate-400 hover:text-slate-600'}`}
                    >
                        Categories
                    </button>
                </div>
            </div>

            {loading ? (
                <div className="flex justify-center py-20">
                    <RiLoader4Line className="animate-spin text-emerald-500 text-3xl" />
                </div>
            ) : (
                <>
                    {activeTab === 'places' ? (
                        <div className="space-y-6">
                            <div className="flex flex-col md:flex-row gap-4 justify-between">
                                <div className="relative w-full md:w-96">
                                    <RiSearchLine className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                                    <input
                                        type="text"
                                        placeholder="Search by name or district..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        className="w-full pl-12 pr-6 py-3 bg-white border border-slate-100 rounded-2xl text-sm font-medium focus:ring-2 focus:ring-emerald-500/20 transition-all"
                                    />
                                </div>
                                <button
                                    onClick={() => { setEditingPlace(null); setShowPlaceModal(true); }}
                                    className="flex items-center gap-2 px-6 py-3 bg-slate-900 text-white rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-emerald-600 transition-all shadow-xl shadow-slate-950/10"
                                >
                                    <RiAddLine size={18} /> Add Destination
                                </button>
                            </div>

                            <div className="bg-white rounded-[32px] border border-slate-100 overflow-hidden shadow-sm">
                                <table className="w-full text-left">
                                    <thead className="bg-slate-50 border-b border-slate-100">
                                        <tr>
                                            <th className="px-8 py-5 text-[10px] font-black uppercase tracking-widest text-slate-400">Destination</th>
                                            <th className="px-8 py-5 text-[10px] font-black uppercase tracking-widest text-slate-400">Region</th>
                                            <th className="px-8 py-5 text-[10px] font-black uppercase tracking-widest text-slate-400">Category</th>
                                            <th className="px-8 py-5 text-[10px] font-black uppercase tracking-widest text-slate-400">Likes</th>
                                            <th className="px-8 py-5 text-[10px] font-black uppercase tracking-widest text-slate-400 text-right">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-50">
                                        {filteredPlaces.map(place => (
                                            <tr key={place.id} className="hover:bg-slate-50/50 transition-colors">
                                                <td className="px-8 py-5">
                                                    <div className="flex items-center gap-4">
                                                        <div className="w-12 h-10 rounded-xl bg-slate-100 overflow-hidden">
                                                            <img src={place.image_url} alt="" className="w-full h-full object-cover" />
                                                        </div>
                                                        <span className="font-bold text-slate-900">{place.name}</span>
                                                    </div>
                                                </td>
                                                <td className="px-8 py-5">
                                                    <span className="text-sm font-bold text-slate-500">{place.district_name}</span>
                                                </td>
                                                <td className="px-8 py-5">
                                                    <span className="bg-emerald-50 text-emerald-600 px-3 py-1 rounded-lg text-[10px] font-black uppercase tracking-widest border border-emerald-100">
                                                        {place.category_name}
                                                    </span>
                                                </td>
                                                <td className="px-8 py-5 font-bold text-slate-900 text-sm">
                                                    {place.likes_count}
                                                </td>
                                                <td className="px-8 py-5 text-right">
                                                    <div className="flex justify-end gap-2">
                                                        <button
                                                            onClick={() => { setEditingPlace(place); setShowPlaceModal(true); }}
                                                            className="p-2.5 text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-xl transition-all"
                                                        >
                                                            <RiEditLine size={18} />
                                                        </button>
                                                        <button
                                                            onClick={() => handleDeletePlace(place.id)}
                                                            className="p-2.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-xl transition-all"
                                                        >
                                                            <RiDeleteBinLine size={18} />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    ) : (
                        <div className="max-w-4xl space-y-8">
                            <div className="flex justify-between items-center bg-white p-8 rounded-[32px] border border-slate-100 shadow-sm">
                                <div>
                                    <h3 className="text-xl font-bold text-slate-900">Categories</h3>
                                    <p className="text-sm text-slate-500 font-medium">Define structural classifications for destinations.</p>
                                </div>
                                <button
                                    onClick={() => setShowCategoryModal(true)}
                                    className="flex items-center gap-2 px-6 py-3 bg-slate-900 text-white rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-emerald-600 transition-all"
                                >
                                    <RiPriceTag3Line size={18} /> New Category
                                </button>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {categories.map(cat => (
                                    <div key={cat.id} className="flex justify-between items-center p-6 bg-white rounded-3xl border border-slate-100 group hover:border-emerald-200 transition-all">
                                        <div className="flex items-center gap-4">
                                            <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center text-emerald-600">
                                                <RiPriceTag3Line size={20} />
                                            </div>
                                            <span className="font-bold text-slate-900">{cat.name}</span>
                                        </div>
                                        <button
                                            onClick={() => handleDeleteCategory(cat.id)}
                                            className="p-2 text-slate-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                                        >
                                            <RiDeleteBinLine size={20} />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </>
            )}

            {/* Place Modal */}
            <AnimatePresence>
                {showPlaceModal && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm overflow-y-auto">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.9, y: 30 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.9, y: 30 }}
                            className="bg-white w-full max-w-4xl rounded-[2.5rem] shadow-2xl my-12"
                        >
                            <div className="flex justify-between items-center p-8 border-b border-slate-100">
                                <div>
                                    <h2 className="text-2xl font-bold text-slate-900">{editingPlace ? 'Edit' : 'Add'} Destination</h2>
                                    <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mt-1">Travel Inventory Node</p>
                                </div>
                                <button onClick={() => { setShowPlaceModal(false); setEditingPlace(null); }} className="p-3 hover:bg-slate-50 rounded-full text-slate-400 transition-colors">
                                    <RiCloseLine size={28} />
                                </button>
                            </div>

                            <form onSubmit={handleSavePlace} className="p-8 grid grid-cols-1 md:grid-cols-2 gap-8">
                                <div className="space-y-6">
                                    <div>
                                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-4 mb-2 block">District</label>
                                        <select
                                            required
                                            value={editingPlace ? editingPlace.district_id : newPlace.district_id}
                                            onChange={(e) => editingPlace ? setEditingPlace({ ...editingPlace, district_id: e.target.value }) : setNewPlace({ ...newPlace, district_id: e.target.value })}
                                            className="w-full px-6 py-4 bg-slate-50 border-none rounded-2xl text-sm font-bold text-slate-700 focus:ring-2 focus:ring-emerald-500/20"
                                        >
                                            <option value="">Select District</option>
                                            {districts.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-4 mb-2 block">Category</label>
                                        <select
                                            required
                                            value={editingPlace ? editingPlace.category_id : newPlace.category_id}
                                            onChange={(e) => editingPlace ? setEditingPlace({ ...editingPlace, category_id: e.target.value }) : setNewPlace({ ...newPlace, category_id: e.target.value })}
                                            className="w-full px-6 py-4 bg-slate-50 border-none rounded-2xl text-sm font-bold text-slate-700 focus:ring-2 focus:ring-emerald-500/20"
                                        >
                                            <option value="">Select Category</option>
                                            {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-4 mb-2 block">Destination Name</label>
                                        <input
                                            type="text"
                                            required
                                            value={editingPlace ? editingPlace.name : newPlace.name}
                                            onChange={(e) => editingPlace ? setEditingPlace({ ...editingPlace, name: e.target.value }) : setNewPlace({ ...newPlace, name: e.target.value })}
                                            className="w-full px-6 py-4 bg-slate-50 border-none rounded-2xl text-sm font-medium focus:ring-2 focus:ring-emerald-500/20"
                                            placeholder="e.g. Horton Plains"
                                        />
                                    </div>
                                    <div>
                                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-4 mb-2 block">Short Description</label>
                                        <textarea
                                            rows={2}
                                            required
                                            value={editingPlace ? editingPlace.description : newPlace.description}
                                            onChange={(e) => editingPlace ? setEditingPlace({ ...editingPlace, description: e.target.value }) : setNewPlace({ ...newPlace, description: e.target.value })}
                                            className="w-full px-6 py-4 bg-slate-50 border-none rounded-2xl text-sm font-medium focus:ring-2 focus:ring-emerald-500/20 resize-none"
                                        />
                                    </div>
                                </div>

                                <div className="space-y-6">
                                    <div>
                                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-4 mb-2 block">Hero Image URL</label>
                                        <input
                                            type="text"
                                            required
                                            value={editingPlace ? editingPlace.image_url : newPlace.image_url}
                                            onChange={(e) => editingPlace ? setEditingPlace({ ...editingPlace, image_url: e.target.value }) : setNewPlace({ ...newPlace, image_url: e.target.value })}
                                            className="w-full px-6 py-4 bg-slate-50 border-none rounded-2xl text-sm font-medium focus:ring-2 focus:ring-emerald-500/20"
                                        />
                                    </div>
                                    <div>
                                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-4 mb-2 block">Google Maps Location (URL or Embed)</label>
                                        <input
                                            type="text"
                                            value={editingPlace ? editingPlace.map_location : newPlace.map_location}
                                            onChange={(e) => editingPlace ? setEditingPlace({ ...editingPlace, map_location: e.target.value }) : setNewPlace({ ...newPlace, map_location: e.target.value })}
                                            className="w-full px-6 py-4 bg-slate-50 border-none rounded-2xl text-sm font-medium focus:ring-2 focus:ring-emerald-500/20"
                                            placeholder="https://maps.google.com/..."
                                        />
                                    </div>
                                    <div>
                                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-4 mb-2 block">Full Content / Article</label>
                                        <textarea
                                            rows={5}
                                            value={editingPlace ? editingPlace.content : newPlace.content}
                                            onChange={(e) => editingPlace ? setEditingPlace({ ...editingPlace, content: e.target.value }) : setNewPlace({ ...newPlace, content: e.target.value })}
                                            className="w-full px-6 py-4 bg-slate-50 border-none rounded-2xl text-sm font-medium focus:ring-2 focus:ring-emerald-500/20 resize-none"
                                            placeholder="Markdown supported content..."
                                        />
                                    </div>
                                </div>

                                <div className="md:col-span-2 flex justify-end gap-4 pt-4">
                                    <button
                                        type="button"
                                        onClick={() => setShowPlaceModal(false)}
                                        className="px-8 py-4 text-xs font-black uppercase tracking-widest text-slate-400 hover:text-slate-600 transition-colors"
                                    >
                                        Go Back
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={saving}
                                        className="px-8 py-4 bg-slate-900 text-white rounded-2xl font-black text-xs uppercase tracking-widest shadow-xl hover:bg-emerald-600 transition-all flex items-center gap-2"
                                    >
                                        {saving ? <RiLoader4Line className="animate-spin" /> : <RiSaveLine />}
                                        Initialize Destination
                                    </button>
                                </div>
                            </form>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>

            {/* Category Modal */}
            <AnimatePresence>
                {showCategoryModal && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.9 }}
                            className="bg-white w-full max-w-md rounded-[2rem] shadow-2xl p-8"
                        >
                            <div className="flex justify-between items-center mb-6">
                                <h2 className="text-xl font-bold text-slate-900">New Category</h2>
                                <button onClick={() => setShowCategoryModal(false)} className="text-slate-400 hover:text-slate-600">
                                    <RiCloseLine size={24} />
                                </button>
                            </div>
                            <form onSubmit={handleSaveCategory} className="space-y-6">
                                <div>
                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-4 mb-2 block">Category Name</label>
                                    <input
                                        type="text"
                                        required
                                        autoFocus
                                        value={newCategory.name}
                                        onChange={(e) => setNewCategory({ name: e.target.value })}
                                        className="w-full px-6 py-4 bg-slate-50 border-none rounded-2xl text-sm font-medium focus:ring-2 focus:ring-emerald-500/20"
                                        placeholder="e.g. Cultural Heritage"
                                    />
                                </div>
                                <button
                                    type="submit"
                                    disabled={saving}
                                    className="w-full py-4 bg-slate-900 text-white rounded-2xl font-black text-xs uppercase tracking-widest shadow-xl hover:bg-emerald-600 transition-all flex items-center justify-center gap-2"
                                >
                                    {saving ? <RiLoader4Line className="animate-spin" /> : <RiSaveLine />}
                                    Create Category
                                </button>
                            </form>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default ManageTravel;
